import React, { useEffect, useState } from 'react';
import { FaTimes, FaEdit, FaSave, FaPlus, FaSpinner } from 'react-icons/fa';
import { useFinancialParams, FinancialParam, OwnerParams } from '../../hooks/data/useFinancialParams';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const FinancialParamsModal: React.FC<Props> = ({ isOpen, onClose }) => {
    const { owners, isLoading, fetchParams, updateParam, createParam } = useFinancialParams();
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const [addingOwner, setAddingOwner] = useState<string | null>(null);
    const [newParamName, setNewParamName] = useState('');
    const [newParamValue, setNewParamValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) fetchParams();
    }, [isOpen, fetchParams]);

    if (!isOpen) return null;

    const handleEdit = (param: FinancialParam) => {
        setEditingId(param.id);
        setEditValue(String(param.param_value));
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const handleSaveEdit = async (id: number) => {
        setIsSaving(true);
        const success = await updateParam(id, Number(editValue));
        if (success) { setEditingId(null); await fetchParams(); }
        setIsSaving(false);
    };

    const handleAddParam = async (ownerName: string) => {
        if (!newParamName.trim() || !newParamValue.trim()) return;
        setIsSaving(true);
        const success = await createParam(ownerName, newParamName, Number(newParamValue));
        if (success) {
            setAddingOwner(null);
            setNewParamName('');
            setNewParamValue('');
            await fetchParams();
        }
        setIsSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Parâmetros Financeiros</h2>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <FaTimes />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <FaSpinner className="animate-spin text-blue-500 text-xl" />
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {owners.map((owner: OwnerParams) => (
                                <div key={owner.owner_name} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 dark:bg-slate-700 px-4 py-2 flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{owner.owner_name}</h3>
                                        <button type="button"
                                            onClick={() => { setAddingOwner(owner.owner_name); setNewParamName(''); setNewParamValue(''); }}
                                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
                                            <FaPlus className="text-[10px]" /> Adicionar
                                        </button>
                                    </div>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-100 dark:border-gray-700">
                                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Parâmetro</th>
                                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">Valor (%)</th>
                                                <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {owner.params.map((param: FinancialParam) => (
                                                <tr key={param.id} className="border-b border-gray-50 dark:border-gray-700 last:border-0">
                                                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{param.param_name}</td>
                                                    <td className="px-4 py-2 text-right">
                                                        {editingId === param.id ? (
                                                            <input type="number" step="any" value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                className="w-24 text-right border border-blue-300 dark:border-blue-500 rounded px-2 py-1 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                autoFocus />
                                                        ) : (
                                                            <span className="text-gray-900 dark:text-gray-100">{param.param_value}%</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 text-right">
                                                        {editingId === param.id ? (
                                                            <div className="flex items-center justify-end gap-1">
                                                                <button type="button" onClick={() => handleSaveEdit(param.id)} disabled={isSaving}
                                                                    className="text-green-600 hover:text-green-700 disabled:opacity-50 p-1">
                                                                    <FaSave className="text-xs" />
                                                                </button>
                                                                <button type="button" onClick={handleCancelEdit}
                                                                    className="text-gray-400 hover:text-gray-600 p-1">
                                                                    <FaTimes className="text-xs" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button type="button" onClick={() => handleEdit(param)}
                                                                className="text-blue-500 hover:text-blue-700 p-1">
                                                                <FaEdit className="text-xs" />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {addingOwner === owner.owner_name && (
                                                <tr className="bg-blue-50 dark:bg-slate-750">
                                                    <td className="px-4 py-2">
                                                        <input type="text" value={newParamName}
                                                            onChange={(e) => setNewParamName(e.target.value)}
                                                            placeholder="nome_parametro"
                                                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" />
                                                    </td>
                                                    <td className="px-4 py-2 text-right">
                                                        <input type="number" step="any" value={newParamValue}
                                                            onChange={(e) => setNewParamValue(e.target.value)}
                                                            placeholder="0.00"
                                                            className="w-24 text-right border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100" />
                                                    </td>
                                                    <td className="px-4 py-2 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button type="button" onClick={() => handleAddParam(owner.owner_name)} disabled={isSaving}
                                                                className="text-green-600 hover:text-green-700 disabled:opacity-50 p-1">
                                                                <FaSave className="text-xs" />
                                                            </button>
                                                            <button type="button" onClick={() => setAddingOwner(null)}
                                                                className="text-gray-400 hover:text-gray-600 p-1">
                                                                <FaTimes className="text-xs" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                    <button type="button" onClick={onClose}
                        className="bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded text-sm px-4 py-2">
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FinancialParamsModal;
